import PropTypes from 'prop-types';
import React, { useState, useMemo, useCallback } from 'react';
import { getURLQueryParams, updateURLQueryParams } from './common/navigation/navigation';

import {
    getClassDataById,
    getDefaultClassId,
    sortClasses,
    isDemoClass,
} from './common/utils/classesHelpers';
import { READING, ESPANOL } from './constants/applications';
import {
    classesType,
    applicationsType,
    classeStudentsType,
    staffType,
    formsType,
    institutionType,
    bmWindowConfigType,
    gatingRulesConfigType,
} from './constants/prop-types';
import './stylesheets/index.scss';
import PopupContextProvider from './common/contexts/PopupContext';
import AppConfigContextProvider from './common/contexts/AppConfigContext';
import LanguageContext, { getLanguage } from './common/contexts/LanguageContext';
import BannerContextProvider from './common/contexts/BannerContext';
import TRCAppContext, { getTRCAppForGroup } from './common/contexts/TRCAppContext';
import { setLanguage, LANGUAGES } from './common/localization';
import Dashboard from './components/dashboard/Dashboard';
import DashboardDataProvider from './components/dashboard/DashboardDataProvider';
import useAppGroupAssessmentType from './common/hooks/useAppGroupAssessmentType';
import useAppGroup from './common/hooks/useAppGroup';
import useBMWindows from './common/hooks/useBMWindows';
import FeatureToggleContext from './common/contexts/FeatureToggleContext';

const App = ({ params }) => {
    // HACK: dashboard uses classeId when user navigates from OWA to dashboard
    // and classId from D8 to dashboard.
    const { classId, classeId, studentId } = getURLQueryParams();
    const receivedClassId = classeId || classId;
    const {
        classes,
        classesStudents,
        applications,
        institution,
        syncTrigger,
        staff,
        isDeviceSupported,
        reloadPage,
        checkAndGetSessionStatus,
        forms,
        isUnsyncedData,
        isSyncSuccess,
        logoutTrigger,
        applicationSpecificCallbacks,
        edition,
        gatingRulesConfig,
        bmWindowConfig,
        benchmarkWindows,
        featureToggle,
        floPMPopupLink,
        isBrowserSupported,
        isSecureAssessmentsEnabled,
    } = params;
    const { appGroup, setAppGroup } = useAppGroup(applications);
    const { windows, currentBMWindow, nextBMWindow, fourthPeriodAvailability } = useBMWindows(
        benchmarkWindows,
        appGroup,
    );

    const sortedClasses = useMemo(() => sortClasses(classes), [classes]);
    const [selectedClassId, selectClassId] = useState(
        getClassDataById(sortedClasses, receivedClassId) != null
            ? receivedClassId
            : getDefaultClassId(sortedClasses),
    );

    const isDemoClassSelected = useMemo(() => isDemoClass(sortedClasses, selectedClassId), [
        selectedClassId,
        sortedClasses,
    ]);

    const [assessmentTypeReading, setAssessmentTypeReading] = useAppGroupAssessmentType(
        windows[READING],
        READING,
    );
    const [assessmentTypeEspanol, setAssessmentTypeEspanol] = useAppGroupAssessmentType(
        windows[ESPANOL],
        ESPANOL,
    );

    const selectedAssessmentType =
        appGroup === READING ? assessmentTypeReading : assessmentTypeEspanol;

    const setApplicationAssessmentType = currentAssessmentType => {
        localStorage.removeItem('mclass_portal_owa_active_tab');
        updateURLQueryParams({ activeTab: undefined });

        if (appGroup === READING) {
            setAssessmentTypeReading(currentAssessmentType);
        } else {
            setAssessmentTypeEspanol(currentAssessmentType);
        }
    };

    const setLocalizationLanguage = useCallback(
        () => setLanguage(appGroup === ESPANOL ? LANGUAGES.SPANISH : LANGUAGES.ENGLISH),
        [appGroup],
    );
    // set localization language on each App render even though it produces new
    // value only on app group change
    // we cannot use useEffect here because it executes *after* render and
    // localized labels are one render behind
    setLocalizationLanguage();

    return (
        <FeatureToggleContext.Provider value={featureToggle}>
            <DashboardDataProvider
                staff={staff}
                forms={forms}
                applications={applications}
                classesStudents={classesStudents}
                selectedClassId={selectedClassId}
                selectedAppGroup={appGroup}
                isUnsyncedData={isUnsyncedData}
                isDemoClassSelected={isDemoClassSelected}
                checkAndGetSessionStatus={checkAndGetSessionStatus}
                applicationSpecificCallbacks={applicationSpecificCallbacks}
                reloadPage={reloadPage}
                gatingRulesConfig={gatingRulesConfig}
                bmWindowConfig={bmWindowConfig}
                appBMWindows={windows[appGroup]}
                currentBMWindow={currentBMWindow}
                fourthPeriodAvailability={fourthPeriodAvailability}
                nextBMWindow={nextBMWindow}
                selectedAssessmentType={selectedAssessmentType}
            >
                <AppConfigContextProvider
                    applications={applications}
                    edition={edition}
                    floPMPopupLink={floPMPopupLink}
                    isSecureAssessmentsEnabled={isSecureAssessmentsEnabled}
                >
                    <LanguageContext.Provider value={getLanguage(appGroup)}>
                        <TRCAppContext.Provider value={getTRCAppForGroup(appGroup)}>
                            <PopupContextProvider>
                                <BannerContextProvider>
                                    <Dashboard
                                        isUnsyncedData={isUnsyncedData}
                                        applications={applications}
                                        studentId={studentId}
                                        institution={institution}
                                        selectClassId={selectClassId}
                                        selectedAppGroup={appGroup}
                                        selectAppGroup={setAppGroup}
                                        setAssessmentType={setApplicationAssessmentType}
                                        sortedClasses={sortedClasses}
                                        isDeviceSupported={isDeviceSupported}
                                        isSyncSuccess={isSyncSuccess}
                                        syncTrigger={syncTrigger}
                                        logoutTrigger={logoutTrigger}
                                        isBrowserSupported={isBrowserSupported}
                                    />
                                </BannerContextProvider>
                            </PopupContextProvider>
                        </TRCAppContext.Provider>
                    </LanguageContext.Provider>
                </AppConfigContextProvider>
            </DashboardDataProvider>
        </FeatureToggleContext.Provider>
    );
};

App.propTypes = {
    params: PropTypes.shape({
        applications: applicationsType,
        staff: staffType,
        classes: classesType.isRequired,
        forms: formsType,
        institution: institutionType,
        classesStudents: classeStudentsType,
        isDeviceSupported: PropTypes.bool,
        isSyncSuccess: PropTypes.bool,
        isBrowserSupported: PropTypes.bool,
        isUnsyncedData: PropTypes.bool,
        reloadPage: PropTypes.func,
        checkAndGetSessionStatus: PropTypes.func,
        syncTrigger: PropTypes.func.isRequired,
        logoutTrigger: PropTypes.func.isRequired,
        applicationSpecificCallbacks: PropTypes.object,
        edition: PropTypes.string,
        floPMPopupLink: PropTypes.string,
        benchmarkWindows: PropTypes.objectOf(
            PropTypes.arrayOf(
                PropTypes.shape({
                    startDate: PropTypes.string.isRequired,
                    endDate: PropTypes.string.isRequired,
                }),
            ).isRequired,
        ).isRequired,
        gatingRulesConfig: gatingRulesConfigType,
        bmWindowConfig: bmWindowConfigType,
        featureToggle: PropTypes.objectOf(PropTypes.bool).isRequired,
        isSecureAssessmentsEnabled: PropTypes.bool,
    }).isRequired,
};

export default App;
